import { slotsForBase } from '@/lib/nflRosterSlots';
import { idpPositions, startablePositions, type SlotEligibilityRow } from '@/lib/nflStartablePositions';

/**
 * SAB-995 / SAB-998 — ONE derivation of "what can this league roster", for every surface.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 *
 * SAB-955 built the startability rule for the draft room. The players page never got it,
 * so the two surfaces disagreed about the same league: the draft room correctly hid IDP
 * tabs for a league with `IDP: 0`, while the players page still offered DB/LB/DL chips
 * that returned nothing on every click. SAB-998 then needed a FLEX chip, which is the same
 * question asked a third time.
 *
 * Three surfaces deriving the same rule separately is how they drift, and a drift here is
 * invisible: a chip that quietly returns nothing looks like a league with no such players
 * rather than like a bug. So the rule lives here once and the surfaces consume it.
 *
 * ── FLEX IS NOT A POSITION ──────────────────────────────────────────────────
 *
 * Every other chip names a position that exists in `nfl_players.position`. FLEX names a
 * SLOT, and the set of positions that can fill it is a league-independent property of
 * `nfl_slot_eligibility` (`slot_base = 'FLEX'` -> FB, RB, TE, WR) intersected with whether
 * this league declares a FLEX starting slot at all.
 *
 * That is why the chip cannot be a client-side filter over a fetched page. SAB-993 moved
 * ownership filtering INTO the RPC for exactly this reason: filtering after pagination
 * silently drops rows, because the server already decided which 50 to send. A multi-position
 * FLEX filter applied client-side would show "12 of 3,393" and be wrong in the same way.
 * The positions travel to the server as `p_positions`.
 */

export interface LeagueRosterability {
  /**
   * Positions with at least one STARTING slot in this league. `null` = eligibility rows
   * have not loaded (or the read failed), which every consumer must read as "do not
   * filter". See `startablePositions` for why empty is lumped in with null.
   */
  startable: Set<string> | null;

  /**
   * Does this league declare at least one FLEX starting slot?
   *
   * Derived from `roster_slots` ALONE, deliberately. `roster_slots` arrives synchronously
   * with the league, while `nfl_slot_eligibility` is an async read — so basing chip
   * PRESENCE on the slots means the filter row does not reflow when the rows land. Only
   * the position SET behind the chip needs the table.
   */
  hasFlex: boolean;

  /**
   * The positions that can fill this league's FLEX slot, or `null` when the eligibility
   * rows have not loaded. Empty when the league has no FLEX slot.
   *
   * `null` rather than a hardcoded {RB, WR, TE, FB} fallback on purpose. A constant here
   * would be a fourth copy of a rule the server owns, and the one that drifts silently:
   * a league adding FB eligibility would keep filtering FB out with no error anywhere.
   * The nflStartablePositions header makes the same argument against NFL_SLOT_POSITION_MAP.
   */
  flex: Set<string> | null;

  /**
   * The positions that map to an IDP slot, or `null` when the rows have not loaded.
   *
   * SAB-812. League-INDEPENDENT, unlike `flex`: the draft pool's IDP tab asks "is this
   * position defensive", which the table answers the same way for every league. Whether
   * this league can start one is `startable`'s question, already asked separately.
   */
  idp: Set<string> | null;
}

/**
 * Positions eligible for a FLEX slot in this league.
 *
 * Returns null when the rows have not loaded, and an EMPTY set when the league has no FLEX
 * starting slot — those are different answers and callers act on them differently, so they
 * must not collapse into one. `slotsForBase` already excludes bench and IR, so a league
 * whose only "FLEX" is a bench row correctly reports none.
 */
export function flexPositions(
  rows: SlotEligibilityRow[] | null | undefined,
  rosterSlots: unknown,
): Set<string> | null {
  if (!rows || rows.length === 0) return null;
  if (slotsForBase('FLEX', rosterSlots).length === 0) return new Set();
  return new Set(rows.filter((r) => r.slot_base === 'FLEX').map((r) => r.position));
}

/** Does this league declare a FLEX starting slot? Synchronous — see `hasFlex`. */
export function leagueHasFlex(rosterSlots: unknown): boolean {
  return slotsForBase('FLEX', rosterSlots).length > 0;
}

/** The whole scoping, from the two inputs every surface already has. */
export function leagueRosterability(
  rows: SlotEligibilityRow[] | null | undefined,
  rosterSlots: unknown,
): LeagueRosterability {
  return {
    startable: startablePositions(rows, rosterSlots),
    hasFlex: leagueHasFlex(rosterSlots),
    flex: flexPositions(rows, rosterSlots),
    idp: idpPositions(rows),
  };
}

/**
 * Scope a surface's position chip list to what this league can actually roster.
 *
 * `chips` are the surface's own values (the players page and the draft room label them
 * differently), and `groupOf` maps a chip to the positions it stands for so a GROUP chip
 * survives when any member is startable — IDP is a group, not a position, and a league
 * with LB slots must keep its IDP tab even though nothing is literally positioned "IDP".
 *
 * `alwaysKeep` covers the "All" / "ALL" entry, which is not a position and must never be
 * filtered out; dropping it would leave a league with no way back to the full pool.
 *
 * A null `startable` returns the list UNTOUCHED. A filter row that renders short and then
 * grows is worse than one that is briefly permissive, and being wrongly restrictive during
 * a live draft is the failure this whole module exists to avoid.
 */
export function scopeChips<T extends string>(
  chips: readonly T[],
  startable: ReadonlySet<string> | null,
  opts: { alwaysKeep?: readonly T[]; groupOf?: (chip: T) => readonly string[] | null } = {},
): T[] {
  if (startable === null) return [...chips];
  const keep = new Set<string>(opts.alwaysKeep ?? []);
  return chips.filter((c) => {
    if (keep.has(c)) return true;
    const group = opts.groupOf?.(c);
    if (group) return group.some((p) => startable.has(p));
    return startable.has(c);
  });
}

/**
 * The FLEX chip's value.
 *
 * A constant rather than a bare 'FLEX' literal because it is load-bearing in three places
 * that must agree: the chip list, the "is this the FLEX chip" test that decides whether the
 * value goes to `p_position` or `p_positions`, and the mobile filter sheet. A typo in any
 * one of them sends 'FLEX' to `p_position`, which matches no player's position and returns
 * an empty page with no error — the silent-empty failure this module exists to prevent.
 */
export const FLEX_CHIP = 'FLEX';

/**
 * The IDP chips are GROUPS, not positions.
 *
 * `nfl_slot_eligibility` maps every IDP position to a single slot_base 'IDP', while the
 * players surfaces show three chips. The table cannot express the DB/LB/DL split, so this
 * map is the one eligibility-shaped constant SAB-812 KEPT: it carries information the
 * source of truth does not hold, rather than restating information it does.
 *
 * What it must never do is disagree with the table about WHICH positions are IDP at all.
 * The union of these three groups is asserted equal to the seed's IDP set in
 * nflSlotEligibility.conformance.test.ts — the check that would have caught SAB-806. Scoping a chip by its own label alone would be asking whether a player is
 * literally positioned "DL", which is a different question from whether this league can
 * start a defensive lineman.
 *
 * Every one of the 13 appears in exactly one group, so a league with an IDP slot keeps all
 * three chips and a league without keeps none — the two cases that actually occur. The map
 * matters for the case that does not yet: a slot template that makes only some IDP
 * positions startable would then scope the chips correctly rather than all-or-nothing.
 */
export const IDP_CHIP_GROUPS: Record<string, readonly string[]> = {
  DB: ['DB', 'CB', 'FS', 'S', 'SS', 'SAF'],
  LB: ['LB', 'MLB', 'OLB'],
  DL: ['DL', 'DE', 'DT', 'NT'],
};
